import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Avatar,
  ClickAwayListener
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  AccountTree as HierarchyIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SplitWorkItemDialog = ({ 
  open, 
  onClose, 
  issue, 
  projectId,
  projectDetails,
  onSplit 
}) => {
  const [splitItems, setSplitItems] = useState([
    { summary: '', storyPoints: '', sprint: 'backlog', issueType: issue?.issueType || 1, assignee: 'Unassigned' }
  ]);
  const [availableSprints, setAvailableSprints] = useState([]);
  const [showSprintWarning, setShowSprintWarning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState('');
  const [previewKeys, setPreviewKeys] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [assigneeDropdownOpen, setAssigneeDropdownOpen] = useState({});
 
  // Issue type mapping
  const issueTypes = {
    1: 'Bug',
    2: 'Epic', 
    3: 'Story',
    4: 'Task',
  };

  // Helper function to get assignee name by employee_id
  const getAssigneeNameById = (employeeId) => {
    if (!employeeId || employeeId === 'Unassigned') return 'Unassigned';
    const member = teamMembers.find(m => m.employee_id === employeeId);
    return member ? (member.name || member.username || member.email || `Member ${employeeId}`) : employeeId;
  };

  // Helper function to get original issue assignee name
  const getOriginalAssigneeName = () => {
    if (!issue?.assignee || issue.assignee === 'Unassigned') return 'Unassigned';
    return getAssigneeNameById(issue.assigneeName);
  };

  useEffect(() => {
    if (open && projectDetails?.key) {
      fetchSprints();
      fetchTeamMembers();
      generatePreviewKeys();
    }
  }, [open, projectDetails, splitItems.length]);

  useEffect(() => {
    // Check if any split item has a different sprint than the original AND is not moving to backlog
    const originalSprintId = issue?.sprintId || 'backlog';
    const hasSprintChange = splitItems.some(item => {
      const itemSprintId = item.sprint || 'backlog';
      // Only show warning if:
      // 1. Moving from one sprint to another sprint (not backlog)
      // 2. Both original and destination are actual sprints (not backlog)
      return itemSprintId !== originalSprintId && 
             itemSprintId !== 'backlog' && 
             originalSprintId !== 'backlog';
    });
    
    setShowSprintWarning(hasSprintChange);
  }, [splitItems, issue]);

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      setTeamMembersLoading(true);
      const response = await fetch(`${API_URL}/api/addteam/project/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
  
          }
        }
      );
      const data = await response.json();
      
      let allMembers = [];
      
      if (data.teamMembers) {
        if (Array.isArray(data.teamMembers)) {
          if (data.teamMembers.length > 0 && Array.isArray(data.teamMembers[0])) {
            allMembers = data.teamMembers.flat();
          } else {
            allMembers = data.teamMembers;
          }
        }
        
        if (data.developers && Array.isArray(data.developers)) {
          allMembers = [...allMembers, ...data.developers];
        }
        if (data.testers && Array.isArray(data.testers)) {
          allMembers = [...allMembers, ...data.testers];
        }
        if (data.managers && Array.isArray(data.managers)) {
          allMembers = [...allMembers, ...data.managers];
        }
        
        setTeamMembers(allMembers);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      //console.error('Error fetching team members:', error);
      setTeamMembers([]);
    } finally {
      setTeamMembersLoading(false);
    }
  };

  const fetchSprints = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/sprints/project/${projectDetails.key}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            
          }
        }
      );
      if (response.data && response.data.data) {
        const sprints = [
          { id: 'backlog', name: 'Backlog' },
          ...response.data.data.map(sprint => ({
            id: sprint.id,
            name: sprint.name || `Sprint ${sprint.id}`
          }))
        ];
        setAvailableSprints(sprints);
      }
    } catch (error) {
      //console.error('Error fetching sprints:', error);
    }
  };

  const generatePreviewKeys = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/issues/project/${projectDetails.key}/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
         
          }
        }
      );
      const allIssues = response.data.data || [];
      
      const baseKey = issue.key;
      const hierarchyLevel = getHierarchyLevel(baseKey);
      
      const childPattern = new RegExp(`^S-${escapeRegExp(baseKey)}\\.\\d+$`);
      
      const existingChildren = allIssues.filter(childIssue => 
        childPattern.test(childIssue.key)
      );
      
      const existingNumbers = existingChildren
        .map(child => {
          const parts = child.key.split('.');
          const lastPart = parts[parts.length - 1];
          return parseInt(lastPart) || 0;
        })
        .filter(num => num > 0)
        .sort((a, b) => a - b);
      
      let nextNumber = 1;
      const keys = [];
      
      for (let i = 0; i < splitItems.length; i++) {
        while (existingNumbers.includes(nextNumber)) {
          nextNumber++;
        }
        
        const newKey = `S-${baseKey}.${nextNumber}`;
        keys.push(newKey);
        existingNumbers.push(nextNumber);
        nextNumber++;
      }
      
      setPreviewKeys(keys);
      
    } catch (error) {
      //console.error('Error generating preview keys:', error);
      const keys = splitItems.map((_, index) => `S-${issue.key}.${index + 1}`);
      setPreviewKeys(keys);
    }
  };

  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const getHierarchyLevel = (key) => {
    const parts = key.split('.');
    return parts.length;
  };

  const inheritFromParent = (parentIssue) => {
    return {
      project: parentIssue.project,
      priority: parentIssue.priority,
      severity: parentIssue.severity,
      assignee: parentIssue.assignee,
      productOwner: parentIssue.productOwner,
      labels: [...(parentIssue.labels || [])],
      version: parentIssue.version,
      fixedInVersion: parentIssue.fixedInVersion,
      timeEstimate: parentIssue.timeEstimate,
      epic_id: parentIssue.epic_id,
      story_id: parentIssue.story_id,
      task_id: parentIssue.task_id,
      parent_id: parentIssue.id,
      hierarchy_level: Math.min(parentIssue.hierarchy_level + 1, 5),
      issueType: parentIssue.issueType,
      status: 1,
      bugStatus: 1,
      startDate: parentIssue.startDate,
      endDate: parentIssue.endDate,
      statusHistory: [],
      attachments: [],
      order: 0
    };
  };

  const addSplitItem = () => {
    setSplitItems([...splitItems, { 
      summary: '', 
      storyPoints: '', 
      sprint: 'backlog',
      issueType: issue?.issueType || 1,
      assignee: 'Unassigned'
    }]);
  };

  const removeSplitItem = (index) => {
    if (splitItems.length > 1) {
      setSplitItems(splitItems.filter((_, i) => i !== index));
    }
  };

  const updateSplitItem = (index, field, value) => {
    const updated = [...splitItems];
    updated[index][field] = value;
    setSplitItems(updated);
  };

  const copyFromOriginal = (index) => {
    const updated = [...splitItems];
    updated[index].summary = issue.summary;
    updated[index].storyPoints = issue.storyPoints || '';
    // Store the employee_id, not the name
    updated[index].assignee = issue.assignee || 'Unassigned';
    setSplitItems(updated);
  };

  const validateSplitItems = () => {
    return splitItems.every(item => item.summary.trim() !== '');
  };

  const getInitials = (name) => {
    if (!name || name === 'Unassigned') return 'U';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 2);
  };

  // Assignee Dropdown Component
  const AssigneeDropdown = ({ assignee, index }) => {
    const handleAssigneeChange = (selectedMember) => {
      let assigneeToSet = 'Unassigned';
      
      if (selectedMember !== 'Unassigned') {
        if (typeof selectedMember === 'object') {
          // Store the employee_id in the state
          assigneeToSet = selectedMember.employee_id;
        } else {
          assigneeToSet = selectedMember;
        }
      }
      
      updateSplitItem(index, 'assignee', assigneeToSet);
      setAssigneeDropdownOpen({ ...assigneeDropdownOpen, [index]: false });
    };

    const handleAssigneeClick = (event) => {
      event.stopPropagation();
      setAssigneeDropdownOpen({ 
        ...assigneeDropdownOpen, 
        [index]: !assigneeDropdownOpen[index] 
      });
    };

    // Display the name, but the actual value stored is employee_id
    const displayAssignee = getAssigneeNameById(assignee);

    return (
      <ClickAwayListener onClickAway={() => setAssigneeDropdownOpen({ ...assigneeDropdownOpen, [index]: false })}>
        <Box sx={{ position: 'relative' }}>
          <Box 
            onClick={handleAssigneeClick}
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minHeight: '32px',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            <Avatar 
              sx={{ 
                width: 20, 
                height: 20, 
                fontSize: '0.75rem',
                bgcolor: displayAssignee !== 'Unassigned' ? '#0052CC' : '#DFE1E6',
                color: displayAssignee !== 'Unassigned' ? '#FFFFFF' : '#172B4D',
                mr: 1
              }}
            >
              {getInitials(displayAssignee)}
            </Avatar>
            <Typography 
              variant="caption" 
              sx={{ 
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {displayAssignee}
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: '14px', ml: 0.5 }} />
          </Box>

          {assigneeDropdownOpen[index] && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                zIndex: 1000,
                maxHeight: '200px',
                overflowY: 'auto',
                mt: 0.5
              }}
            >
              {/* Unassigned option */}
              <Box
                onClick={() => handleAssigneeChange('Unassigned')}
                sx={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f8f9fa'
                  },
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: assignee === 'Unassigned' ? '#e3f2fd' : 'transparent'
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    fontSize: '0.75rem',
                    bgcolor: '#DFE1E6',
                    color: '#172B4D',
                    mr: 1
                  }}
                >
                  {getInitials('Unassigned')}
                </Avatar>
                <Typography variant="body2">
                  Unassigned
                </Typography>
              </Box>
              
              {/* Team members */}
              {teamMembersLoading ? (
                <Box sx={{ padding: '12px', textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </Box>
              ) : (
                teamMembers.map((member, memberIndex) => {
                  const memberName = member.name || member.username || member.email || `Member ${memberIndex + 1}`;
                  const isSelected = assignee === member.employee_id;
                  
                  return (
                    <Box
                      key={member.employee_id || member.id || memberIndex}
                      onClick={() => handleAssigneeChange(member)}
                      sx={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#f8f9fa'
                        },
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: isSelected ? '#e3f2fd' : 'transparent'
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          width: 24, 
                          height: 24, 
                          fontSize: '0.75rem',
                          bgcolor: '#0052CC',
                          color: '#FFFFFF',
                          mr: 1
                        }}
                      >
                        {getInitials(memberName)}
                      </Avatar>
                      <Typography variant="body2">
                        {memberName}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          )}
        </Box>
      </ClickAwayListener>
    );
  };

  const handleSplit = async () => {
    if (!validateSplitItems()) {
      alert('Please provide summary for all split items');
      return;
    }

    setLoading(true);
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const inheritedProps = inheritFromParent(issue);
      
      const splitData = {
        originalIssueId: issue.id,
        splitItems: splitItems.map((item, index) => ({
          summary: item.summary,
          sprintId: item.sprint === 'backlog' ? null : item.sprint,
          issueType: item.issueType,
          key: previewKeys[index] || `${issue.key}.${index + 1}`,
          ...inheritedProps,
          // Store employee_id in database, not the name
          assignee: item.assignee !== 'Unassigned' ? item.assignee : null,
          storyPoints: item.storyPoints !== '' ? parseFloat(item.storyPoints) || null : null,
          summary: item.summary,
          sprintId: item.sprint === 'backlog' ? null : item.sprint,
          description: `Split from ${issue.key}: ${issue.summary}`,
          file_path: generateFilePath(previewKeys[index] || `${issue.key}.${index + 1}`, inheritedProps.hierarchy_level)
        }))
      };

      const response = await axios.post(`${API_URL}/api/issues/split`, splitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.data.status === 'success') {
        onSplit(response.data.data);
        onClose();
      } else {
        throw new Error(response.data.message || 'Failed to split work item');
      }
    } catch (error) {
      //console.error('Error splitting work item:', error);
      alert('Failed to split work item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateFilePath = (key, hierarchyLevel) => {
    const pathParts = key.split(/[-\.]/);
    const projectKey = pathParts[0];
    const hierarchyPath = pathParts.slice(1).join('/');
    return `${projectKey}/${hierarchyPath}`;
  };

  const getSprintWarningMessage = () => {
    return 'Sprint scope may be affected by moving items between different sprints.';
  };

  const getHierarchyInfo = () => {
    const currentLevel = getHierarchyLevel(issue.key);
    const newLevel = currentLevel;
    const maxLevel = 5;
    
    return {
      currentLevel,
      newLevel,
      canSplit: newLevel <= maxLevel,
      levelName: ['Root', 'Epic', 'Story', 'Task', 'Bug', 'Subtask'][newLevel] || 'Max Level'
    };
  };

  const hierarchyInfo = getHierarchyInfo();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HierarchyIcon />
          <Typography variant="h6">Split work item</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Hierarchy Warning */}
        {!hierarchyInfo.canSplit && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Maximum hierarchy level reached. Cannot split further than level {hierarchyInfo.currentLevel}.
          </Alert>
        )}

        {/* Original Issue Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={issue?.key} 
              size="small" 
              variant="outlined" 
              sx={{ fontFamily: 'monospace' }}
            />
            Original Issue (Level {hierarchyInfo.currentLevel})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {issue?.summary}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Type: {issueTypes[issue?.issueType]} |
            Sprint: {issue?.sprintId ? `Sprint ${issue.sprintId}` : 'Backlog'} |
            Assignee: {getOriginalAssigneeName()} |
            Story Points: {issue?.storyPoints || 'None'}
          </Typography>
        </Box>

        {/* Key Preview */}
        {previewKeys.length > 0 && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon fontSize="small" />
              New  Keys Preview (Level {hierarchyInfo.newLevel})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {previewKeys.map((key, index) => (
                <Chip 
                  key={index}
                  label={key} 
                  size="small" 
                  variant="outlined"
                  color="primary"
                  sx={{ fontFamily: 'monospace' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Sprint Warning - Only show when moving between actual sprints */}
        {showSprintWarning && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 3 }}
          >
            {getSprintWarningMessage()}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Split Items Table */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">New work items ({hierarchyInfo.levelName}s)</Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addSplitItem}
              size="small"
              variant="outlined"
              disabled={!hierarchyInfo.canSplit}
            >
              Add another
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="30%">Summary</TableCell>
                <TableCell width="12%">Story Points</TableCell>
                <TableCell width="18%">Assignee</TableCell>
                <TableCell width="15%">Move to</TableCell>
                <TableCell width="15%">Preview Key</TableCell>
                <TableCell width="10%">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {splitItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter summary"
                      value={item.summary}
                      onChange={(e) => updateSplitItem(index, 'summary', e.target.value)}
                      required
                      error={!item.summary.trim()}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Points"
                      value={item.storyPoints}
                      onChange={(e) => updateSplitItem(index, 'storyPoints', e.target.value)}
                      type="number"
                      inputProps={{ min: 0, step: 0.5 }}
                    />
                  </TableCell>
                  <TableCell>
                    <AssigneeDropdown assignee={item.assignee} index={index} />
                  </TableCell>
                  <TableCell>
                    <Select
                      fullWidth
                      size="small"
                      value={item.sprint}
                      onChange={(e) => updateSplitItem(index, 'sprint', e.target.value)}
                    >
                      {availableSprints.map((sprint) => (
                        <MenuItem key={sprint.id} value={sprint.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {sprint.id !== 'backlog' && (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'success.main'
                                }}
                              />
                            )}
                            {sprint.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={previewKeys[index] || `${issue?.key}.${index + 1}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => copyFromOriginal(index)}
                        title="Copy from original"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                      {splitItems.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeSplitItem(index)}
                          title="Remove item"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Comprehensive Inheritance Info */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
          <Typography variant="body2" color="success.main">
            <strong>Complete Inheritance:</strong> Split items will inherit ALL properties from {issue?.key}:
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSplit} 
          variant="contained" 
          disabled={loading || !validateSplitItems() || !hierarchyInfo.canSplit}
        >
          {loading ? 'Splitting...' : `Split into ${splitItems.length} ${hierarchyInfo.levelName}s`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitWorkItemDialog;